// src/pages/NomCalculoPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import Toast, { useToast } from "../components/Toast";
import {
  obtenerNominas, obtenerNomina, actualizarNominaDetalle, obtenerConceptos,
} from "../services/nominaService";

const money = (n) => Number(n ?? 0).toLocaleString("es-MX", { style: "currency", currency: "MXN" });

export default function NomCalculoPage() {
  const { id: periodoId } = useParams();
  const navigate = useNavigate();
  const { toasts, closeToast, success: ok, error: err } = useToast();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey(k => k + 1);

  const [showModal, setShowModal] = useState(false);
  const [editNomina, setEditNomina] = useState(null);
  const [conceptos, setConceptos] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    obtenerNominas(periodoId)
      .then(data => { if (!cancelled) setRows(data ?? []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [periodoId, reloadKey]);

  useEffect(() => {
    obtenerConceptos(null).then(setConceptos).catch(() => {});
  }, []);

  const openEdit = async (nominaId) => {
    try {
      const n = await obtenerNomina(nominaId);
      setEditNomina(n);
      setShowModal(true);
    } catch { err("Error al cargar nómina"); }
  };

  const addConcepto = (val) => {
    if (!val) return;
    const c = JSON.parse(val);
    setEditNomina(prev => ({
      ...prev,
      detalle: [...(prev.detalle ?? []), {
        conceptoId: c.id, clave: c.clave, descripcion: c.nombre,
        tipoConcepto: c.tipoConcepto, importe: 0, gravaImss: c.gravaImss, gravaIsr: c.gravaIsr
      }]
    }));
  };

  const removeDetalle = (idx) => {
    setEditNomina(prev => ({ ...prev, detalle: prev.detalle.filter((_, i) => i !== idx) }));
  };

  const updateDetalleImporte = (idx, val) => {
    setEditNomina(prev => {
      const detalle = [...prev.detalle];
      detalle[idx] = { ...detalle[idx], importe: val };
      return { ...prev, detalle };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await actualizarNominaDetalle(editNomina.id, {
        DiasLaborados: editNomina.diasLaborados,
        HorasExtra: editNomina.horasExtra,
        Detalle: (editNomina.detalle ?? []).map(d => ({
          Id: d.id, ConceptoId: d.conceptoId,
          Clave: d.clave, Descripcion: d.descripcion,
          TipoConcepto: d.tipoConcepto, Importe: parseFloat(d.importe) || 0,
          GravaImss: d.gravaImss, GravaIsr: d.gravaIsr
        }))
      });
      ok("Nómina actualizada");
      setShowModal(false);
      reload();
    } catch { err("Error al guardar"); }
    finally { setSaving(false); }
  };

  const totalPerc = rows.reduce((s, r) => s + (r.totalPercepciones ?? 0), 0);
  const totalDed  = rows.reduce((s, r) => s + (r.totalDeducciones ?? 0), 0);
  const totalIsr  = rows.reduce((s, r) => s + (r.isr ?? 0), 0);
  const totalImss = rows.reduce((s, r) => s + (r.imssObrero ?? 0), 0);
  const totalNeto = rows.reduce((s, r) => s + (r.netoPagar ?? 0), 0);

  const columns = [
    { key: "clave", title: "Clave", style: { width: 70, fontWeight: 600 } },
    { key: "nombreCompleto", title: "Nombre" },
    { key: "departamento", title: "Depto.", style: { width: 120 } },
    { key: "diasLaborados", title: "Días", style: { width: 55, textAlign: "center" } },
    { key: "totalPercepciones", title: "Percepciones", render: r => money(r.totalPercepciones), style: { width: 120, textAlign: "right" } },
    { key: "totalDeducciones", title: "Deducciones",  render: r => money(r.totalDeducciones),  style: { width: 110, textAlign: "right" } },
    { key: "isr",             title: "ISR",           render: r => money(r.isr),               style: { width: 90,  textAlign: "right" } },
    { key: "imssObrero",      title: "IMSS",          render: r => money(r.imssObrero),         style: { width: 90,  textAlign: "right" } },
    { key: "netoPagar", title: "Neto a Pagar", render: r => <strong>{money(r.netoPagar)}</strong>, style: { width: 120, textAlign: "right" } },
    {
      key: "_acc", title: "",
      render: r => (
        <div style={{ display: "flex", gap: 4 }}>
          <button className="lmd-btn lmd-btn-xs lmd-btn-outline" onClick={() => openEdit(r.id)}>Editar</button>
          <button className="lmd-btn lmd-btn-xs lmd-btn-outline" onClick={() => navigate(`/nomina/recibo/${r.id}`)}>Recibo</button>
        </div>
      ),
      style: { width: 120 },
    },
  ];

  return (
    <div>
      <Toast toasts={toasts} onClose={closeToast} />
      <PageHeader title="Cálculo de Nómina" subtitle={`Periodo #${periodoId}`} />

      <div style={{ marginBottom: 12 }}>
        <button className="lmd-btn lmd-btn-outline" onClick={() => navigate("/nomina/periodos")}>
          ← Periodos
        </button>
      </div>

      <DataTable columns={columns} rows={rows} keyField="id" loading={loading} />

      {rows.length > 0 && (
        <div style={{ marginTop: 16, padding: "12px 16px", background: "#f8fafc", borderRadius: 8, border: "1px solid var(--clr-border)", display: "flex", gap: 32, justifyContent: "flex-end", fontSize: 13 }}>
          <span><strong>Percepciones:</strong> {money(totalPerc)}</span>
          <span><strong>Deducciones:</strong> {money(totalDed)}</span>
          <span><strong>ISR:</strong> {money(totalIsr)}</span>
          <span><strong>IMSS:</strong> {money(totalImss)}</span>
          <span style={{ fontSize: 15 }}><strong>Total Neto: {money(totalNeto)}</strong></span>
        </div>
      )}

      {showModal && editNomina && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 900, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 10, padding: 28, width: "98%", maxWidth: 680, position: "relative", maxHeight: "90vh", overflowY: "auto" }}>
            <button onClick={() => setShowModal(false)} style={{ position: "absolute", top: 12, right: 16, border: "none", background: "none", fontSize: 20, cursor: "pointer" }}>✕</button>
            <h3 style={{ margin: "0 0 4px", fontSize: 16 }}>Editar Nómina</h3>
            <p style={{ margin: "0 0 16px", fontSize: 13, color: "var(--clr-text-muted)" }}>{editNomina.nombreCompleto}</p>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px", marginBottom: 16 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Días Laborados</label>
                <input className="lmd-input" type="number" step="0.5" min="0" value={editNomina.diasLaborados}
                  onChange={e => setEditNomina(p => ({ ...p, diasLaborados: e.target.value }))}
                  style={{ width: "100%" }} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Horas Extra</label>
                <input className="lmd-input" type="number" step="0.5" min="0" value={editNomina.horasExtra}
                  onChange={e => setEditNomina(p => ({ ...p, horasExtra: e.target.value }))}
                  style={{ width: "100%" }} />
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <strong style={{ fontSize: 13 }}>Conceptos</strong>
              <select className="lmd-select" style={{ width: 260 }} value=""
                onChange={e => { addConcepto(e.target.value); e.target.value = ""; }}>
                <option value="">+ Agregar concepto...</option>
                {conceptos.map(c => (
                  <option key={c.id} value={JSON.stringify(c)}>
                    {c.clave} — {c.nombre} ({c.tipoConcepto === "P" ? "Perc." : "Ded."})
                  </option>
                ))}
              </select>
            </div>

            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 16 }}>
              <thead>
                <tr style={{ background: "#f1f5f9" }}>
                  <th style={{ padding: "6px 8px", textAlign: "left" }}>Clave</th>
                  <th style={{ padding: "6px 8px", textAlign: "left" }}>Descripción</th>
                  <th style={{ padding: "6px 8px", textAlign: "center", width: 70 }}>Tipo</th>
                  <th style={{ padding: "6px 8px", textAlign: "right", width: 120 }}>Importe</th>
                  <th style={{ width: 36 }}></th>
                </tr>
              </thead>
              <tbody>
                {(editNomina.detalle ?? []).map((d, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "4px 8px" }}>{d.clave}</td>
                    <td style={{ padding: "4px 8px" }}>{d.descripcion}</td>
                    <td style={{ padding: "4px 8px", textAlign: "center" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: d.tipoConcepto === "P" ? "#16a34a" : "#dc2626" }}>
                        {d.tipoConcepto === "P" ? "Perc." : "Ded."}
                      </span>
                    </td>
                    <td style={{ padding: "4px 8px" }}>
                      <input className="lmd-input" type="number" step="0.01" min="0" value={d.importe}
                        onChange={e => updateDetalleImporte(i, e.target.value)}
                        style={{ width: 110, textAlign: "right" }} />
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button onClick={() => removeDetalle(i)} style={{ border: "none", background: "none", color: "#dc2626", cursor: "pointer", fontSize: 16 }}>✕</button>
                    </td>
                  </tr>
                ))}
                {(editNomina.detalle ?? []).length === 0 && (
                  <tr><td colSpan={5} style={{ padding: 16, textAlign: "center", color: "var(--clr-text-muted)" }}>Sin conceptos. Agrega uno del combo.</td></tr>
                )}
              </tbody>
            </table>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button type="button" className="lmd-btn lmd-btn-outline" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="lmd-btn lmd-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
